import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import FeaturedAdsCarousel from '../components/FeaturedAdsCarousel';
import PopupAd from '../components/PopupAd';
import './BusinessListPage.css';

function BusinessListPage() {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubcat, setSelectedSubcat] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch category details
        const categoryDoc = await getDoc(doc(db, 'categories', categoryId));
        if (categoryDoc.exists()) {
          setCategory({ id: categoryDoc.id, ...categoryDoc.data() });
          console.log('Loaded category:', categoryDoc.data().name);
        } else {
          console.error('Category not found:', categoryId);
        }

        // Fetch subcategories - use categoryId filter
        let subcatQuery = query(
          collection(db, 'subcategories'),
          where('categoryId', '==', categoryId)
        );
        let subcatSnapshot = await getDocs(subcatQuery);
        
        // Filter for active subcategories in code
        const subcatsData = subcatSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(sub => sub.isActive !== false && sub.active !== false);
        
        subcatsData.sort((a, b) => (a.order || 0) - (b.order || 0));
        setSubcategories(subcatsData);
        console.log('Loaded subcategories:', subcatsData.length);

        // Fetch all businesses for this category - use 'business' collection
        const businessesQuery = query(
          collection(db, 'business'),
          where('categoryId', '==', categoryId)
        );
        const businessesSnapshot = await getDocs(businessesQuery);
        const businessesData = businessesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBusinesses(businessesData);
        setFilteredBusinesses(businessesData);
        console.log('Loaded businesses:', businessesData.length);
      } catch (error) {
        console.error('Error fetching data:', error);
        console.error('Error details:', error.message);
        // Set empty arrays on error
        setCategory(null);
        setSubcategories([]);
        setBusinesses([]);
        setFilteredBusinesses([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categoryId]);

  useEffect(() => {
    let filtered = businesses;

    // Filter by subcategory - use subCategoryId (camelCase as per Firebase)
    if (selectedSubcat) {
      filtered = filtered.filter(b => b.subCategoryId === selectedSubcat);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(business =>
        business.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        business.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        business.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredBusinesses(filtered);
  }, [searchTerm, selectedSubcat, businesses]);

  return (
    <div className="business-list-page">
      {category && category.bannerUrl && (
        <div className="category-banner">
          <img src={category.bannerUrl} alt={category.name} />
          <div className="banner-overlay">
            <h1>{category.name}</h1>
            {category.description && <p>{category.description}</p>}
          </div>
        </div>
      )}

      <div className="page-content">
        <div className="page-header">
          <Link to="/categories" className="back-link">← All Categories</Link>
          <div className="search-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-bar"
              placeholder="Search businesses, locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="clear-search" 
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <FeaturedAdsCarousel categoryId={categoryId} subcategoryId={selectedSubcat} />
        <PopupAd categoryId={categoryId} subcategoryId={selectedSubcat} />

        {subcategories.length > 0 && (
          <div className="subcategories-filter">
            <button 
              className={`subcat-btn ${!selectedSubcat ? 'active' : ''}`}
              onClick={() => setSelectedSubcat(null)}
            >
              All
            </button>
            {subcategories.map(subcat => (
              <button
                key={subcat.id}
                className={`subcat-btn ${selectedSubcat === subcat.id ? 'active' : ''}`}
                onClick={() => setSelectedSubcat(subcat.id)}
              >
                {subcat.name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading businesses...</p>
          </div>
        ) : (
          <>
            <div className="results-count">
              <p>
                {subcategories.length > 0 && selectedSubcat && (
                  <span>
                    {subcategories.find(s => s.id === selectedSubcat)?.name}: {' '}
                  </span>
                )}
                {filteredBusinesses.length} {filteredBusinesses.length === 1 ? 'business' : 'businesses'} found
              </p>
            </div>
            <div className="businesses-grid">
              {filteredBusinesses.length > 0 ? (
                filteredBusinesses.map(business => (
                  <Link key={business.id} to={`/business/${business.id}`} className="business-card-new">
                    <div className="business-image-wrapper">
                      {(() => {
                        const imageUrl = business.shopBanner || 
                                        (business.shopBanners && business.shopBanners.length > 0 ? business.shopBanners[0] : null) ||
                                        (business.images && business.images.length > 0 ? business.images[0] : null) ||
                                        business.bannerUrl ||
                                        (business.venueImages && business.venueImages.length > 0 ? business.venueImages[0] : null) ||
                                        business.logoUrl;
                        
                        return imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={business.name} 
                            className="business-image"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/discover-logo.png';
                              e.target.style.objectFit = 'contain';
                              e.target.style.padding = '20px';
                              e.target.style.opacity = '0.5';
                            }}
                          />
                        ) : (
                          <div className="business-placeholder">
                            <img src="/discover-logo.png" alt="Discover Cook Islands" style={{ height: '60px', opacity: 0.5 }} />
                          </div>
                        );
                      })()}
                    </div>
                    <div className="business-content">
                      <h3>{business.name}</h3>
                      {business.location && (
                        <p className="business-location">
                          <span>📍</span> {business.location}
                        </p>
                      )}
                      {business.phone && (
                        <p className="business-phone">
                          <span>📞</span> {business.phone}
                        </p>
                      )}
                      {business.email && (
                        <p className="business-email">
                          <span>📧</span> {business.email}
                        </p>
                      )}
                      {business.description && (
                        <p className="description">{business.description.substring(0, 150)}...</p>
                      )}
                      {business.openingHours && Array.isArray(business.openingHours) && business.openingHours.length > 0 && (
                        <p className="opening-hours">
                          <span>🕒</span> {business.openingHours[0].day}: {business.openingHours[0].from} - {business.openingHours[0].to}
                        </p>
                      )}
                    </div>
                  </Link>
                ))
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <h3>No businesses found</h3>
                  <p>Try adjusting your search or filters</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BusinessListPage;
